import { type FormEvent, useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Container, Input, Logo } from '@/design-system';
import { api, ApiError } from '@/lib/api';
import { REDIRECT_ERROR_KEY } from '@/lib/auth/AuthProvider';
import { useAuth } from '@/lib/auth/useAuth';
import { IS_ENTRA_SIGN_IN_ENABLED } from '@/lib/config';
import { prepareEntra, requestEntraIdToken } from '@/lib/entra';
import styles from './SignInPage.module.css';

/** The accounts the dev profile seeds (DevUserSeeder), one per role. */
const DEVELOPMENT_ACCOUNTS = [
  { email: 'ada.admin@remittance-intake.invalid', name: 'Ada Admin', role: 'Administrator' },
  { email: 'rita.reviewer@remittance-intake.invalid', name: 'Rita Reviewer', role: 'Reviewer' },
  { email: 'vera.viewer@remittance-intake.invalid', name: 'Vera Viewer', role: 'Viewer' },
] as const;

interface Failure {
  message: string;
  requestId: string | undefined;
}

/** What /api/auth/methods reports: the ways in the backend actually accepts. */
interface Methods {
  microsoft: boolean;
  development: boolean;
}

/** A refusal left by a redirect sign-in completed elsewhere; read once. */
function takeRedirectFailure(): Failure | null {
  try {
    const message = sessionStorage.getItem(REDIRECT_ERROR_KEY);
    if (message === null) return null;
    sessionStorage.removeItem(REDIRECT_ERROR_KEY);
    return { message, requestId: undefined };
  } catch {
    return null;
  }
}

/**
 * The way in.
 *
 * Offers what the backend accepts, asked of the backend: Microsoft in any
 * deployment, and the development email sign-in only when the backend runs the dev
 * profile. The frontend cannot know the backend's profile, and offering a way in
 * the backend will refuse fails in a way nobody can read.
 *
 * Laid out as FxComplianceEngine's sign-in: one column, no card, the explanation
 * before the button rather than after a refusal.
 */
export function SignInPage() {
  const { me, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/home';

  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(takeRedirectFailure);
  const [methods, setMethods] = useState<Methods | null>(null);
  const [methodsFailed, setMethodsFailed] = useState(false);

  useEffect(() => {
    // MSAL must be initialised before the click: a popup may only open in the
    // direct user gesture, and an await inside the handler breaks that. See entra.ts.
    prepareEntra();
    let cancelled = false;
    api
      .get<Methods>('/api/auth/methods')
      .then((answer) => {
        if (!cancelled) setMethods(answer);
      })
      .catch(() => {
        if (!cancelled) setMethodsFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (me && !busy) {
    return <Navigate to={from} replace />;
  }

  async function attempt(directoryToken: string) {
    setBusy(true);
    setFailure(null);
    try {
      await signIn(directoryToken);
      navigate(from, { replace: true });
    } catch (error) {
      setFailure({
        message: error instanceof Error ? error.message : 'Sign-in could not be completed.',
        requestId: error instanceof ApiError ? error.requestId : undefined,
      });
    } finally {
      setBusy(false);
    }
  }

  /** The token MSAL returns goes to the same endpoint the development address does. */
  async function withMicrosoft() {
    setBusy(true);
    setFailure(null);
    try {
      await attempt(await requestEntraIdToken());
    } catch (error) {
      setFailure({
        message: error instanceof Error ? error.message : 'Microsoft sign-in could not be completed.',
        requestId: undefined,
      });
      setBusy(false);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    void attempt(email.trim());
  }

  return (
    <Container>
      <main className={styles.page}>
        <div className={styles.panel}>
          <Logo />
          <h1 className={styles.heading}>Sign in</h1>
          <p className={styles.lede}>
            For the advisory team. Sign-on links an existing account. It does not create
            one, so an address nobody has provisioned is refused rather than admitted as a
            Viewer.
          </p>
          <p className={styles.note}>
            Applying for a licence? <Link to="/apply/signin">Sign in with your email address</Link>.
          </p>

          {failure && (
            <Alert tone="danger" title="Refused">
              {failure.message}
              {failure.requestId && (
                <>
                  {' '}
                  Request id: <span className="numeric">{failure.requestId}</span>.
                </>
              )}
            </Alert>
          )}

          {methods === null && !methodsFailed && (
            <p className={styles.note}>Finding out how this deployment signs people in.</p>
          )}
          {methodsFailed && (
            <Alert tone="danger" title="The application could not be reached">
              Sign-in is unavailable until it can. Try again in a moment.
            </Alert>
          )}

          {methods?.microsoft &&
            (IS_ENTRA_SIGN_IN_ENABLED ? (
              <>
                <Button block disabled={busy} onClick={() => void withMicrosoft()}>
                  {busy ? 'Signing in' : 'Sign in with Microsoft'}
                </Button>
                <p className={styles.note}>
                  This deployment verifies sign-in against Microsoft Entra. There is no other
                  way in: an address typed here would not carry a signature, and the
                  application refuses what it cannot verify.
                </p>
              </>
            ) : (
              <Alert tone="danger" title="Microsoft sign-in is not configured here">
                This deployment signs in with Microsoft, but the frontend was built without
                VITE_ENTRA_TENANT_ID and VITE_ENTRA_CLIENT_ID. They must match the
                backend&apos;s ENTRA_TENANT_ID and ENTRA_CLIENT_ID.
              </Alert>
            ))}

          {methods?.development && (
            <>
              <form onSubmit={submit} className={styles.form}>
                <Input
                  label="Directory account"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="name@example.invalid"
                  autoComplete="username"
                />
                <Button type="submit" block disabled={busy || email.trim() === ''}>
                  {busy ? 'Signing in' : 'Sign in'}
                </Button>
              </form>

              <section aria-labelledby="development" className={styles.identities}>
                <h2 id="development" className={styles.identitiesHeading}>
                  Development accounts
                </h2>
                <p className={styles.identitiesNote}>
                  Seeded by the development profile, which accepts an email address in place
                  of a Microsoft sign-in. The application refuses to start this way anywhere
                  else.
                </p>
                <ul className={styles.identityList}>
                  {DEVELOPMENT_ACCOUNTS.map((account) => (
                    <li key={account.email}>
                      <button
                        type="button"
                        className={styles.identity}
                        disabled={busy}
                        onClick={() => void attempt(account.email)}
                      >
                        <span className={styles.identityName}>{account.name}</span>
                        <span className={styles.identityRole}>{account.role}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            </>
          )}

          {/* A way out that is not signing in. After a redirect sign-in the browser's
              back button leads to Microsoft, so without this there is none. */}
          <Link to="/" className={styles.back}>
            Back to the overview
          </Link>
        </div>
      </main>
    </Container>
  );
}
